import { MobileMemoryFallback } from '@/app/ui/MobileMemoryFallback';
import { EmptyPage } from '@/shared/components/EmptyPage';
import { Button } from '@/shared/shadcn/ui/button';
import { trackEvent } from '@/shared/utils/analyticsEvents';
import { CrossCircledIcon } from '@radix-ui/react-icons';
import { useEffect } from 'react';

/**
 * Identifies likely out-of-memory errors coming from WASM / the Rust core.
 *
 * Uses stack-trace pattern matching because the `RuntimeError: unreachable`
 * thrown by WASM on OOM is generic - we need the allocator frames to tell
 * an OOM apart from other panics. Also detects cascaded OOM where
 * `__wbindgen_malloc` becomes the first failing WASM frame after an initial
 * allocation failure.
 */
export const isOutOfMemoryError = (error: Error | unknown): boolean => {
  const errorString = error instanceof Error ? `${error.message}\n${error.stack ?? ''}` : String(error);
  const allocationPatterns = [
    'alloc::raw_vec::handle_error',
    'alloc::raw_vec::RawVec',
    'alloc::alloc::handle_alloc_error',
    'out of memory',
    'memory allocation',
  ];
  if (allocationPatterns.some((pattern) => errorString.includes(pattern))) {
    return true;
  }
  if (errorString.includes('unreachable')) {
    const lines = errorString.split('\n');
    const firstWasmFrame = lines.find((line) => line.includes('quadratic_core.wasm.'));
    if (firstWasmFrame?.includes('__wbindgen_malloc')) {
      return true;
    }
  }
  return false;
};

interface Props {
  error: Error | unknown;
  source?: string;
  // When true, OOM errors render the MobileMemoryFallback "open on desktop"
  // screen instead of the generic desktop reload screen.
  showMobileFallbackOnOOM?: boolean;
}

/**
 * Renders an appropriate error screen for a core-worker error.
 *
 * - OOM on mobile (when `showMobileFallbackOnOOM`): MobileMemoryFallback.
 * - OOM on desktop: "Out of memory" EmptyPage with Reload action.
 * - Anything else: "Quadratic crashed" EmptyPage with Reload action.
 */
export function QuadraticCoreErrorScreen({ error, source, showMobileFallbackOnOOM }: Props) {
  const isOOM = isOutOfMemoryError(error);

  // OOM events are tracked centrally by `installMobileOomHandler` via the
  // global `coreError` listener, so we only emit the non-OOM crash signal
  // here to avoid double-counting.
  useEffect(() => {
    if (!isOOM) {
      trackEvent('[coreError].shown', { source, isOOM: false });
    }
  }, [source, isOOM]);

  if (isOOM && showMobileFallbackOnOOM) {
    return <MobileMemoryFallback reason="outOfMemory" />;
  }

  return (
    <EmptyPage
      title={isOOM ? 'Out of memory' : 'Quadratic crashed'}
      description={
        isOOM
          ? 'Your browser ran out of memory. This can happen with large files or complex operations. Try reloading and working with smaller datasets, or contact support if you need help.'
          : 'Something went wrong. Our team has been notified of this issue. Please reload the application to continue.'
      }
      Icon={CrossCircledIcon}
      actions={<Button onClick={() => window.location.reload()}>Reload</Button>}
      error={error}
      source={source}
    />
  );
}
